/**
 * The frame renderer of the wall's Pinned rows: live frames as solid
 * paper/ink tiles at screen scale, never focused, with no resize handles,
 * window menu or frame chrome (the tile's hover pill is the chrome; a top
 * row is a calm title line). Each tile owns its box, so a body never writes
 * canvas geometry. A right-click opens the wall's own menu for the tile, as
 * its pill's ⋯ does (the multi-selection's when the tile is selected); a
 * tile with nothing to offer lets the click through.
 */
import { createContext, createElement, useContext, useEffect } from "react";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import {
  plainFrameRenderer,
  type FrameItemContextMenuProps,
  type FrameRenderer,
} from "@/components/shapes/shared/frame-renderer-context";
import { cardMenuOpens } from "./card-menu-items";
import { wallFrame } from "./wall-frame-source";

type WallTileMenuHandler = (frameId: string, anchor: ContextMenuAnchor) => void;

const WallTileMenuContext = createContext<WallTileMenuHandler | null>(null);

/** The wall's menu opener (`WallView`'s `onMenu`) for its Pinned tiles. */
export const WallTileMenuProvider = WallTileMenuContext.Provider;

/** Hands the right-click to the wall's menu and closes its own anchor. */
function WallTileContextMenu({
  frame,
  anchor,
  onClose,
}: FrameItemContextMenuProps) {
  const openMenu = useContext(WallTileMenuContext);
  const frameId = frame.frameId;
  useEffect(() => {
    if (!anchor) return;
    openMenu?.(frameId, anchor);
    onClose();
  }, [anchor, frameId, onClose, openMenu]);
  return null;
}

export const wallTileFrameRenderer: FrameRenderer = {
  ...plainFrameRenderer,
  material: "solid",
  independentSurface: true,
  chrome: "none",
  contextMenu: {
    opens: (frame) => {
      const shown = wallFrame(frame.frameId);
      return !!shown && cardMenuOpens(shown);
    },
    render: (props) => createElement(WallTileContextMenu, props),
  },
};
